import { AgeGroup, useGetTimetablesQuery } from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { useMemo } from 'react';

import { useSchool } from '../../../../hooks/useSchool';
import useSchoolYears, { getDefaultYear } from '../../../../hooks/useSchoolYears';
import {
  AttendanceFrequencyRowData,
  buildAttendanceFrequencyRows,
} from './attendance-frequencies-helpers';

export const useAttendanceFrequencies = (): {
  configuredRows: AttendanceFrequencyRowData[];
  unconfiguredAgeGroups: AgeGroup[];
  unconfiguredLabels: string[];
  isLoading: boolean;
} => {
  const { schoolId = '' } = useSchool();

  const {
    activeAgeGroups,
    schoolLevels,
    isLoading: isLoadingAgeGroups,
  } = useAgeGroups(
    {
      schoolId,
      userType: SchoolUserRole.Student,
    },
    { refetchOnMount: 'always' },
  );

  const { schoolYears, isLoading: isLoadingYears } = useSchoolYears();
  const currentYearId = getDefaultYear(schoolYears)?.id ?? '';

  const { data: timetablesData, isLoading: isLoadingTimetables } = useGetTimetablesQuery(
    { school_id: schoolId, school_year_id: currentYearId },
    { enabled: !!schoolId && !!currentYearId, refetchOnMount: 'always' },
  );

  const { configuredRows, unconfiguredAgeGroups, unconfiguredLabels } = useMemo(() => {
    const timetables = timetablesData?.timetables ?? [];
    const structures = timetablesData?.structures ?? [];

    return buildAttendanceFrequencyRows(activeAgeGroups, timetables, structures, schoolLevels);
  }, [activeAgeGroups, schoolLevels, timetablesData?.structures, timetablesData?.timetables]);

  return {
    configuredRows,
    unconfiguredAgeGroups,
    unconfiguredLabels,
    isLoading: isLoadingAgeGroups || isLoadingTimetables || isLoadingYears,
  };
};
